import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, Loader2, Lock, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AppShell from '@/components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HEAR_ABOUT_OPTIONS = [
  'Google Search',
  'LinkedIn',
  'Referral from a colleague',
  'EYB Consulting website',
  'Social media',
  'Event or webinar',
  'Other'
];

const LeadCaptureScreen = ({ assessment, onLeadCaptured }) => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [source, setSource] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!assessment) {
      navigate('/');
    }
  }, [assessment, navigate]);

  if (!assessment) return null;

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim() || !isValidEmail || !source) {
      toast.error('Please complete all fields with a valid email address');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/lead/submit`, {
        assessment_id: assessment.id,
        full_name: fullName.trim(),
        email: email.trim(),
        hear_about_us: source
      });
      onLeadCaptured({ full_name: fullName.trim(), email: email.trim() });
      navigate('/results');
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white border border-slate-200 border-t-4 border-t-[#004B87] p-8 lg:p-12 shadow-sm"
          data-testid="lead-capture-card"
        >
          <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-slate-500 mb-3">
            Almost There
          </div>
          <h1 className="font-display text-3xl lg:text-[38px] font-light leading-tight text-[#0A192F] mb-4">
            Your Business Resilience Index is ready
          </h1>
          <p className="text-slate-600 text-[15px] leading-relaxed max-w-xl mb-10">
            Share a few details so we can send you tailored insights and a copy of your report.
            Your assessment for <b>{assessment.business_name}</b> in the <b>{assessment.industry}</b> industry is
            queued and will be revealed on the next screen.
          </p>

          <form onSubmit={handleSubmit} className="space-y-7">
            <div className="space-y-2">
              <Label htmlFor="lead_full_name" className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">
                Full Name
              </Label>
              <Input
                id="lead_full_name"
                data-testid="lead-name-input"
                placeholder="Jane Smith"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="h-12 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-[#0EA5E9] focus-visible:ring-offset-0 focus-visible:border-[#004B87] text-[15px]"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lead_email" className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">
                Email Address
              </Label>
              <Input
                id="lead_email"
                type="email"
                data-testid="lead-email-input"
                placeholder="jane@company.com.au"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-[#0EA5E9] focus-visible:ring-offset-0 focus-visible:border-[#004B87] text-[15px]"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">
                How did you hear about us?
              </Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger
                  data-testid="lead-source-select"
                  className="h-12 rounded-sm border-slate-300 focus:ring-2 focus:ring-[#0EA5E9] focus:border-[#004B87] text-[15px] shadow-none"
                >
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  {HEAR_ABOUT_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt} data-testid={`lead-source-${opt.replace(/\s+/g, '-').toLowerCase()}`}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 text-[12px] text-slate-500 pt-2">
              <Lock className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.75} />
              We respect your privacy. Your details are shared only with EYB Consulting.
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                data-testid="lead-submit-button"
                disabled={isSubmitting || !fullName.trim() || !isValidEmail || !source}
                className="w-full h-12 rounded-sm bg-[#004B87] hover:bg-[#0A192F] text-white text-[13px] uppercase tracking-[0.18em] font-semibold shadow-none transition-colors disabled:opacity-40 disabled:cursor-not-allowed group"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Preparing your report...
                  </>
                ) : (
                  <>
                    Submit &amp; View Results
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                  </>
                )}
              </Button>
            </div>

            <div className="flex items-center gap-2 text-[12px] text-emerald-700 pt-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Assessment complete — one final step to unlock your insights.
            </div>
          </form>
        </motion.div>
      </div>
    </AppShell>
  );
};

export default LeadCaptureScreen;
